import { readableToApi, checkPermission } from '../../../common/utils/helpers'

/**
 * Checks if user has permission to use a specific product
 * @param {Object} params - Permission check parameters
 * @param {boolean} params.is_follow_up - Whether this is a follow-up order
 * @param {Object} params.productPermissions - Product permissions object
 * @param {Object} params.role - User role object
 * @param {string} params.productId - Product ID to check
 * @returns {boolean} True if user has permission
 */
export const checkProductPermission = ({ is_follow_up, productPermissions, role, productId }) => {
  const orderType = is_follow_up ? 'follow_up' : 'primary'
  if (productPermissions[productId]) {
    return productPermissions[productId][`${readableToApi(role.name)}_${orderType}_order`]
  } else {
    // If we have a product in the list that does not show up in permissions, then that means that it was deleted at some point,
    // so just give the user the ability to edit it if they really want.
    return true
  }
}

/**
 * Checks if user has permission to edit job type
 * @param {Object} params - Permission check parameters
 * @param {string} params.job_type - Current job type
 * @param {boolean} params.is_using_amc - Whether using AMC
 * @param {boolean} params.is_amc_lender - Whether user is AMC lender
 * @param {boolean} params.is_follow_up - Whether this is a follow-up order
 * @param {boolean} params.is_reassign_order - Whether this is a reassign order
 * @returns {boolean} True if user can edit job type
 */
export const checkJobTypeEditPermission = ({
  job_type,
  is_using_amc,
  is_amc_lender,
  is_follow_up,
  is_reassign_order,
}) => {
  if (
    job_type !== 'va' &&
    !is_using_amc &&
    !is_amc_lender &&
    job_type !== 'amc' &&
    !is_reassign_order &&
    checkPermission('order_can_edit_job_type')
  ) {
    return true
  }

  return false
}

/**
 * Checks if the appraiser selection tab should be permitted
 * Sometimes we want to enable the appraiser tab, but disable actual appraiser assignment
 * This is why logic for the tab permission and the actual appraiser assignment permission are separated
 * @param {Object} params - Permission check parameters
 * @param {string} params.job_type - Current job type
 * @param {boolean} params.is_follow_up - Whether this is a follow-up order
 * @param {boolean} params.is_reassign_order - Whether this is a reassign order
 * @param {Object} params.orderData - Order data object
 * @param {boolean} params.is_amc_lender - Whether user is AMC lender
 * @param {boolean} params.integrationMode - Whether in integration mode
 * @param {Array} params.selected_products - Array of selected products
 * @returns {boolean} True if appraiser selection tab is permitted
 */
export const appraiserSelectionTabPermitted = ({
  job_type,
  is_follow_up,
  is_reassign_order,
  orderData,
  is_amc_lender,
  integrationMode,
  selected_products,
}) => {
  return (
    ((job_type === 'manually' && !is_follow_up && checkPermission('order_can_edit_assignment_non_followup')) ||
      (job_type === 'manually' &&
        is_follow_up &&
        checkPermission('order_can_edit_assignment_on_create_follow_up')) ||
      (job_type === 'automatically' &&
        is_reassign_order &&
        orderData &&
        orderData.accepted &&
        ['accepted', 'inspection_scheduled'].includes(orderData.statusKey)) ||
      (job_type === 'amc' &&
        is_amc_lender &&
        (!orderData ||
          ['finding_appraisers', 'scheduling_inspection', 'waiting_for_payment'].includes(orderData.statusKey) ||
          !orderData.accepted ||
          is_reassign_order)) ||
      integrationMode) &&
    selected_products[0] !== null
  )
}
